import { useEffect, useState, type FormEvent } from 'react'
import Layout from '@/components/Layout'
import { fetchUsers, adminCreateUser, adminUpdateUserRole, adminDeleteUser } from '@/lib/api'
import type { AdminUser } from '@/lib/api'

export default function UsersList() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newRole, setNewRole] = useState('owner')
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)

  async function load() { setLoading(true); try { setUsers(await fetchUsers()) } finally { setLoading(false) } }
  useEffect(() => { load() }, [])

  async function handleCreate(e: FormEvent) {
    e.preventDefault(); setError(''); setCreating(true)
    try {
      await adminCreateUser(newEmail, newPassword, newRole)
      setShowCreate(false); setNewEmail(''); setNewPassword(''); setNewRole('owner')
      await load()
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Creation failed') }
    finally { setCreating(false) }
  }

  async function handleRoleChange(userId: string, newRole: string) {
    try { await adminUpdateUserRole(userId, newRole); await load() }
    catch (err: unknown) { alert(err instanceof Error ? err.message : 'Failed') }
  }

  async function handleDelete(userId: string, email: string) {
    if (!window.confirm(`Delete user "${email}"? This cannot be undone.`)) return
    try { await adminDeleteUser(userId); await load() }
    catch (err: unknown) { alert(err instanceof Error ? err.message : 'Failed') }
  }

  const action = (
    <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all duration-200 shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M12 3a4 4 0 100 8 4 4 0 000-8zM22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
      Add User
    </button>
  )

  return (
    <Layout title="Users" action={action}>
      <div className="max-w-4xl mx-auto space-y-5">
        {/* Create form */}
        {showCreate && (
          <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Create New User</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email *</label>
                  <input type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="user@email.com" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Password *</label>
                  <input type="password" required minLength={6} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="Min 6 characters" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Role</label>
                  <select value={newRole} onChange={e => setNewRole(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500">
                    <option value="owner">Owner</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              {error && <p className="text-red-600 text-sm font-medium">{error}</p>}
              <div className="flex gap-3">
                <button type="submit" disabled={creating} className="bg-brand-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors">
                  {creating ? 'Creating...' : 'Create User'}
                </button>
                <button type="button" onClick={() => setShowCreate(false)} className="text-sm font-medium text-slate-600 px-4 py-2.5 rounded-xl border border-surface-200 hover:bg-surface-50">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Users table */}
        <div className="bg-white rounded-2xl border border-surface-200 shadow-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-50/50">
                <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">User</th>
                <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider hidden md:table-cell">Role</th>
                <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider hidden lg:table-cell">Created</th>
                <th className="text-center px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider hidden md:table-cell">Confirmed</th>
                <th className="text-right px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {loading ? (
                [1,2,3].map(i => (<tr key={i} className="animate-pulse"><td className="px-5 py-4"><div className="h-4 w-48 bg-surface-100 rounded" /></td><td className="px-5 py-4"><div className="h-4 w-16 bg-surface-100 rounded" /></td><td className="px-5 py-4"><div className="h-4 w-24 bg-surface-100 rounded" /></td><td className="px-5 py-4"><div className="h-4 w-12 bg-surface-100 rounded mx-auto" /></td><td className="px-5 py-4"><div className="h-4 w-20 bg-surface-100 rounded ml-auto" /></td></tr>))
              ) : users.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-16 text-center"><p className="text-sm font-medium text-slate-600">No users found</p></td></tr>
              ) : (
                users.map(u => (
                  <tr key={u.user_id} className="hover:bg-surface-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center text-xs font-bold text-white shadow flex-shrink-0">
                          {u.email[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{u.email}</p>
                          {u.last_sign_in && <p className="text-[11px] text-slate-400">Last: {new Date(u.last_sign_in).toLocaleDateString()}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <select value={u.role} onChange={e => handleRoleChange(u.user_id, e.target.value)}
                        className={`text-[12px] font-semibold px-2.5 py-1 rounded-full border focus:outline-none focus:ring-2 focus:ring-brand-500/20 cursor-pointer ${
                          u.role === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}>
                        <option value="owner">Owner</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-[13px] text-slate-500 font-medium">
                      {new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-5 py-4 text-center hidden md:table-cell">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${u.email_confirmed ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.email_confirmed ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        {u.email_confirmed ? 'Confirmed' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => handleDelete(u.user_id, u.email)}
                        className="text-[12px] font-medium text-slate-500 hover:text-red-600 bg-surface-100 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  )
}
